import React from 'react';

// ─── Dashboard palette ────────────────────────────────────────────────────────
const C = {
  bg:     '#08101e',
  bg1:    '#0c1525',
  bg2:    '#101b2e',
  bg3:    '#141f33',
  border: '#1e2d42',
  text:   '#e8edf5',
  body:   '#b8c8dc',
  meta:   '#6a7d95',
  green:  '#00e896',
  cyan:   '#00c8ff',
  amber:  '#ffb340',
  red:    '#ff4466',
  blue:   '#4488ff',
  purple: '#9966ff',
  font:   "'DM Mono', ui-monospace, monospace",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const pnlColor  = n => n > 0 ? C.green  : n < 0 ? C.red  : C.meta;
const pnlBg     = n => n > 0 ? `${C.green}0f` : n < 0 ? `${C.red}0f` : 'transparent';
const pnlBorder = n => n > 0 ? `${C.green}30` : n < 0 ? `${C.red}30` : C.border;

const fmt = n => {
  const abs  = Math.abs(n);
  const sign = n >= 0 ? '+' : '−';
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(2)}K`;
  return `${sign}$${abs.toFixed(2)}`;
};

const fmtPlain = n => {
  const abs  = Math.abs(n);
  const sign = n < 0 ? '−' : '';
  return `${sign}$${abs.toFixed(2)}`;
};

// ─── Dot ─────────────────────────────────────────────────────────────────────
const Dot = ({ color }) => (
  <span style={{
    display: 'inline-block', width: 5, height: 5,
    borderRadius: '50%', backgroundColor: color,
    marginRight: 3, verticalAlign: 'middle',
  }} />
);

// ─── Segment ──────────────────────────────────────────────────────────────────
const Seg = ({ label, children }) => (
  <div style={{
    display: 'flex', flexDirection: 'column', justifyContent: 'center',
    padding: '10px 18px', flex: 1,
  }}>
    <div style={{ fontFamily: C.font, fontSize: 9, color: C.meta, letterSpacing: '1px', marginBottom: 4 }}>
      {label}
    </div>
    {children}
  </div>
);

// ─── Main ─────────────────────────────────────────────────────────────────────
function PositionLedgerSummary({ summary }) {
  const totalPnL  = parseFloat(summary?.total_pnl_num  ?? summary?.total_pnl  ?? 0) || 0;
  const stockPnL  = parseFloat(summary?.stock_pnl_num  ?? summary?.stock_pnl  ?? 0) || 0;
  const optionPnL = parseFloat(summary?.option_pnl_num ?? summary?.option_pnl ?? 0) || 0;

  const activeStocks  = summary?.active_stocks  || 0;
  const closedStocks  = summary?.closed_stocks  || 0;
  const activeOptions = summary?.active_options || 0;
  const closedOptions = summary?.closed_options || 0;
  const totalActive   = summary?.total_active   || 0;
  const totalClosed   = summary?.total_closed   || 0;

  // Allocation split bar
  const total  = Math.abs(stockPnL) + Math.abs(optionPnL);
  const eqPct  = total > 0 ? (Math.abs(stockPnL)  / total) * 100 : 50;
  const optPct = 100 - eqPct;

  return (
    <>
      <style>{`
        @keyframes pl-pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.3; }
        }
      `}</style>

      <div style={{
        display: 'flex', alignItems: 'stretch', gap: 1,
        background: C.bg1, border: `1px solid ${C.border}`,
        borderRadius: 8, overflow: 'hidden', marginBottom: 14,
        fontFamily: C.font,
      }}>

        {/* ── Total P&L ──────────────────────────────────────────────────────── */}
        <div style={{
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          padding: '14px 22px', minWidth: 160,
          background: pnlBg(totalPnL),
          borderRight: `1px solid ${pnlBorder(totalPnL)}`,
        }}>
          <div style={{ fontSize: 9, color: C.meta, letterSpacing: '1.2px', marginBottom: 4 }}>
            DAY P&L
          </div>
          <div style={{ fontSize: 22, fontWeight: 500, color: pnlColor(totalPnL), lineHeight: 1 }}>
            {fmt(totalPnL)}
          </div>
          <div style={{ fontSize: 9, color: C.meta, marginTop: 5 }}>
            {totalActive} open · {totalClosed} realized
          </div>
        </div>

        {/* ── Equities ───────────────────────────────────────────────────────── */}
        <Seg label="EQUITIES">
          <div style={{ fontSize: 15, fontWeight: 500, color: pnlColor(stockPnL), lineHeight: 1.1 }}>
            {fmtPlain(stockPnL)}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 4, fontSize: 9, color: C.meta }}>
            <span><Dot color={C.green} />{activeStocks} live</span>
            <span><Dot color={C.meta}  />{closedStocks} closed</span>
          </div>
        </Seg>

        <div style={{ width: 1, background: C.border, flexShrink: 0 }} />

        {/* ── Options ────────────────────────────────────────────────────────── */}
        <Seg label="OPTIONS">
          <div style={{ fontSize: 15, fontWeight: 500, color: pnlColor(optionPnL), lineHeight: 1.1 }}>
            {fmtPlain(optionPnL)}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 4, fontSize: 9, color: C.meta }}>
            <span><Dot color={C.purple} />{activeOptions} live</span>
            <span><Dot color={C.meta}   />{closedOptions} closed</span>
          </div>
        </Seg>

        <div style={{ width: 1, background: C.border, flexShrink: 0 }} />

        {/* ── Split bar ──────────────────────────────────────────────────────── */}
        <Seg label="SPLIT">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
            <div style={{ display: 'flex', height: 4, borderRadius: 2, overflow: 'hidden', gap: 1 }}>
              <div style={{ width: `${eqPct}%`,  background: pnlColor(stockPnL),  borderRadius: '2px 0 0 2px' }} />
              <div style={{ width: `${optPct}%`, background: C.purple, borderRadius: '0 2px 2px 0' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: C.meta }}>
              <span>EQ {eqPct.toFixed(0)}%</span>
              <span>OPT {optPct.toFixed(0)}%</span>
            </div>
          </div>
        </Seg>

        <div style={{ width: 1, background: C.border, flexShrink: 0 }} />

        {/* ── Counts ─────────────────────────────────────────────────────────── */}
        <div style={{
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          padding: '10px 20px', gap: 6, minWidth: 120,
        }}>
          {[
            { label: 'ACTIVE',   count: totalActive,  color: C.green },
            { label: 'REALIZED', count: totalClosed,  color: C.meta  },
            { label: 'TOTAL',    count: totalActive + totalClosed, color: C.body },
          ].map(({ label, count, color }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: C.font, fontSize: 9, color: C.meta, flex: 1 }}>{label}</span>
              <span style={{
                fontFamily: C.font, fontSize: 10, fontWeight: 500,
                color, background: `${color}18`,
                padding: '1px 7px', borderRadius: 3, minWidth: 24, textAlign: 'center',
              }}>
                {count}
              </span>
            </div>
          ))}
        </div>

        {/* ── Live pulse ─────────────────────────────────────────────────────── */}
        <div style={{
          display: 'flex', flexDirection: 'column',
          justifyContent: 'center', alignItems: 'center',
          padding: '10px 18px', borderLeft: `1px solid ${C.border}`, gap: 5,
        }}>
          <div style={{
            width: 6, height: 6, borderRadius: '50%',
            background:  totalActive > 0 ? C.green : C.meta,
            boxShadow:   totalActive > 0 ? `0 0 8px ${C.green}80` : 'none',
            animation:   totalActive > 0 ? 'pl-pulse 2s infinite' : 'none',
          }} />
          <div style={{
            fontFamily: C.font, fontSize: 8, letterSpacing: '1px',
            color: totalActive > 0 ? C.green : C.meta,
          }}>
            {totalActive > 0 ? 'LIVE' : 'IDLE'}
          </div>
        </div>

      </div>
    </>
  );
}

export default PositionLedgerSummary;